// Header/Footer layout grid — shared type definitions (AC-937 / Stage 0).
//
// A header or footer can be composed as a `layout`: ordered rows, each a set of columns
// (grid tracks), each column holding one or more typed elements. This file is TYPES ONLY —
// no rendering, no behavior. It is strictly additive: absent `layout` ⇒ today's fixed
// header/footer rendering (see the fail-safe gate in AC-939). Every node reuses the existing
// ISearchElementStyle shape so theming is unchanged.
//
// Storage: a `layout` lives as a sub-key of the existing headerConfig / footerConfig JSON
// blobs, so nothing about the API / BFF / view models changes.

import { ISearchElementStyle } from './searchPageConfig';

// The kind of element a cell holds. Selects its renderer (and, later, its typed props).
export type LayoutElementType =
    | 'logo' | 'title' | 'text' | 'link' | 'linkGroup' | 'menu'
    | 'search' | 'cart' | 'account' | 'social' | 'contact'
    | 'image' | 'spacer' | 'divider' | 'html';

// Horizontal distribution of a row's tracks / a cell's contents.
export type LayoutAlign = 'left' | 'center' | 'right' | 'between' | 'around';
// Vertical alignment within a row / cell.
export type LayoutVAlign = 'top' | 'center' | 'bottom';

// One placed element. `props` is intentionally generic for now (Stage 0); element-specific
// prop shapes are introduced alongside each element's renderer in later stages.
export interface ILayoutElement {
    id: string;
    type: LayoutElementType;
    props?: Record<string, unknown>;
    style?: ISearchElementStyle;
}

// A grid track. `width` maps to a CSS grid track: a number is px, or a string like
// 'auto' | '1fr' | '25%'. A column usually holds one element but may stack a few.
export interface ILayoutColumn {
    width?: number | string;
    align?: LayoutAlign;
    vAlign?: LayoutVAlign;
    gap?: number;
    hideOnMobile?: boolean;
    elements: ILayoutElement[];
    style?: ISearchElementStyle;
}

export interface ILayoutRow {
    columns: ILayoutColumn[];
    align?: LayoutAlign;
    vAlign?: LayoutVAlign;
    gap?: number;
    style?: ISearchElementStyle;
}

// Responsive behavior. Default (undefined) ⇒ auto-stack below the breakpoint. `rows` lets a
// merchant author an explicit small-screen arrangement when auto-stack isn't enough.
export interface ILayoutResponsive {
    breakpoint?: number;
    strategy?: 'stack' | 'custom';
    rows?: ILayoutRow[];
}

// A full header or footer layout. Header and footer share this one type.
export interface ILayout {
    rows: ILayoutRow[];
    maxWidth?: number;          // content cap; the bar background may still be full-bleed
    style?: ISearchElementStyle; // the bar itself
    mobile?: ILayoutResponsive;
}

const ELEMENT_TYPES: ReadonlySet<string> = new Set<LayoutElementType>([
    'logo', 'title', 'text', 'link', 'linkGroup', 'menu',
    'search', 'cart', 'account', 'social', 'contact',
    'image', 'spacer', 'divider', 'html',
]);

// The fail-safe gate for the whole feature (AC-939).
//
// A layout is "ACTIVE" only when it is a valid, non-empty structure: an object with a `rows`
// array holding at least one row, and at least one row has a `columns` array with at least one
// column whose `elements` array holds at least one element with a KNOWN `type`. Anything else —
// missing, malformed JSON, `{}`, `{rows:[]}`, rows with empty columns, columns with empty or
// unknown-type-only elements — is treated as NOT active, so the caller falls back to the legacy
// header/footer. This is what guarantees a bad or empty blob can never blank the bar.
//
// Accepts either an already-parsed candidate (headerConfig.layout, since headerConfig is parsed
// from its JSON blob) or a raw JSON string; returns the layout when active, else null.
export function parseLayout(candidate: unknown): ILayout | null {
    let obj: unknown = candidate;
    if (typeof candidate === 'string') {
        try { obj = JSON.parse(candidate); } catch { return null; }
    }
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return null;

    const layout = obj as Partial<ILayout>;
    if (!Array.isArray(layout.rows) || layout.rows.length === 0) return null;

    const renderable = layout.rows.some(row =>
        row && Array.isArray(row.columns) && row.columns.some(col =>
            col && Array.isArray(col.elements) && col.elements.some(el =>
                !!el && typeof el === 'object' && ELEMENT_TYPES.has((el as ILayoutElement).type),
            ),
        ),
    );
    return renderable ? (obj as ILayout) : null;
}

// Convenience predicate: true when the candidate is an active layout (see parseLayout).
export function hasActiveLayout(candidate: unknown): boolean {
    return parseLayout(candidate) !== null;
}
